/** GEOMETRIA SCHIȚEI DE VOLUMETRIE — funcții pure, în metri, în reperul lotului.
 *
 *  Reperul e cel al lui `contur_stereo70`: perechi `[N, E]`. Un volum e descris
 *  de PARAMETRI (centrul brațului principal, orientarea, lățimea, brațele,
 *  capătul și partea aripilor) din care se calculează conturul; motorul citește
 *  numai conturul (`poly`), ecranul editează parametrii. Așa un L rămâne un L
 *  când îl tragi de un braț, iar lățimea rămâne banda modulelor.
 *
 *  ORIENTAREA. `unghi_deg` e unghiul brațului principal în planul (N, E), ca la
 *  motor (`schita.unghi_dominant`: atan2(ΔE, ΔN)): `u = (cos θ, sin θ)` pe (N, E).
 *  Perpendiculara `w = (−sin θ, cos θ)`. Ecranul desenează cu nordul în sus
 *  (x = E, y = −N), dar asta e treaba lui, nu a geometriei.
 *
 *  ARIPILE se suprapun cu brațul principal exact pe pătratul de colț lățime ×
 *  lățime, ca în motor (`_Body`): lungimea unei aripi include colțul. */

import type { FormaVolum, VolumSchita } from "../dosarclient";

export type Pct = [number, number];

/** Banda modulelor din catalog: adâncimea implicită a oricărui volum. */
export const LATIME_IMPLICITA_M = 15.3;
/** Un braț nu poate fi mai scurt decât un modul de capăt + nucleu; motorul cere 25. */
export const BRAT_MIN_M = 25;
export const BRAT_MAX_M = 120;
/** O aripă nu poate fi mai scurtă decât colțul ei plus un capăt. */
export const ARIPA_MIN_M = 22;
export const LATIME_MIN_M = 12;
export const LATIME_MAX_M = 18;
/** Raza de lipire a unghiului, grade. */
export const LIPIRE_UNGHI_DEG = 4;

const rad = (g: number) => (g * Math.PI) / 180;

export type ParametriVolum = Omit<VolumSchita, "poly">;

/** Conturul unui volum din parametrii lui, în reperul lotului, fără puncte
 *  coliniare, în ordinea în care se desenează. */
export function poligonVolum(v: ParametriVolum): Pct[] {
  const h = v.latime_m / 2;
  const L = v.brate_m[0] ?? BRAT_MIN_M;
  // în reperul local (a de-a lungul brațului, b pe perpendiculară), cu
  // aripile pe partea b > 0; `parte` oglindește la sfârșit
  let local: Pct[];
  if (v.forma === "bara" || v.brate_m.length < 2) {
    local = [[-L / 2, -h], [L / 2, -h], [L / 2, h], [-L / 2, h]];
  } else {
    // un U cu aripile L1 (capătul −1) și L2 (capătul +1); un L e un U cu o
    // singură aripă (cealaltă = lățimea, adică doar colțul)
    const lat = v.latime_m;
    let l1: number, l2: number;
    if (v.forma === "L") {
      const a = Math.max(lat, v.brate_m[1]);
      l1 = v.capat === -1 ? a : lat;
      l2 = v.capat === 1 ? a : lat;
    } else {
      l1 = Math.max(lat, v.brate_m[1]);
      l2 = Math.max(lat, v.brate_m[2] ?? v.brate_m[1]);
    }
    local = [
      [-L / 2, -h], [L / 2, -h],
      [L / 2, -h + l2], [L / 2 - lat, -h + l2],
      [L / 2 - lat, h], [-L / 2 + lat, h],
      [-L / 2 + lat, -h + l1], [-L / 2, -h + l1],
    ];
  }
  if (v.parte === -1) local = local.map(([a, b]) => [a, -b] as Pct).reverse();
  local = faraColiniare(local);
  const c = Math.cos(rad(v.unghi_deg)), s = Math.sin(rad(v.unghi_deg));
  return local.map(([a, b]) => [
    v.centru[0] + a * c - b * s,
    v.centru[1] + a * s + b * c,
  ] as Pct);
}

function faraColiniare(p: Pct[]): Pct[] {
  const out: Pct[] = [];
  for (let i = 0; i < p.length; i++) {
    const a = p[(i + p.length - 1) % p.length], b = p[i], c = p[(i + 1) % p.length];
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    const dupl = Math.abs(b[0] - a[0]) < 1e-9 && Math.abs(b[1] - a[1]) < 1e-9;
    if (!dupl && Math.abs(cross) > 1e-9) out.push(b);
  }
  return out;
}

/** Un volum nou al formei date, așezat cu centrul unde se cere. */
export function volumNou(forma: FormaVolum, centru: Pct, unghi_deg = 0, id?: string): VolumSchita {
  const brate = forma === "bara" ? [50] : forma === "L" ? [50, 30] : forma === "U" ? [60, 30, 30] : [60, 36, 26];
  const v: ParametriVolum = {
    id: id ?? `v${Math.random().toString(36).slice(2, 8)}`,
    forma, centru, unghi_deg, latime_m: LATIME_IMPLICITA_M, brate_m: brate, capat: 1, parte: 1,
  };
  return { ...v, poly: poligonVolum(v) };
}

/** Același volum, cu parametrii schimbați și conturul refăcut. */
export function cuParametri(v: VolumSchita, p: Partial<ParametriVolum>): VolumSchita {
  const n: ParametriVolum = { ...v, ...p };
  n.brate_m = n.brate_m.map((x, i) => Math.min(i === 0 ? BRAT_MAX_M : BRAT_MAX_M,
                                               Math.max(i === 0 ? BRAT_MIN_M : ARIPA_MIN_M, x)));
  n.latime_m = Math.min(LATIME_MAX_M, Math.max(LATIME_MIN_M, n.latime_m));
  n.unghi_deg = ((n.unghi_deg % 180) + 180) % 180;
  return { ...n, poly: poligonVolum(n) };
}

export function aria(p: Pct[]): number {
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length];
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2;
}

export function centroid(p: Pct[]): Pct {
  let sx = 0, sy = 0, sa = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length];
    const cr = a[0] * b[1] - b[0] * a[1];
    sx += (a[0] + b[0]) * cr; sy += (a[1] + b[1]) * cr; sa += cr;
  }
  if (Math.abs(sa) < 1e-12) return [p.reduce((s, q) => s + q[0], 0) / p.length, p.reduce((s, q) => s + q[1], 0) / p.length];
  return [sx / (3 * sa), sy / (3 * sa)];
}

export function cutie(p: Pct[]): { n0: number; n1: number; e0: number; e1: number } {
  let n0 = Infinity, n1 = -Infinity, e0 = Infinity, e1 = -Infinity;
  for (const [n, e] of p) { n0 = Math.min(n0, n); n1 = Math.max(n1, n); e0 = Math.min(e0, e); e1 = Math.max(e1, e); }
  return { n0, n1, e0, e1 };
}

export function inPoligon(p: Pct, ring: Pct[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a[1] > p[1]) !== (b[1] > p[1])
        && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}

function segInters(a: Pct, b: Pct, c: Pct, d: Pct): boolean {
  const o = (p: Pct, q: Pct, r: Pct) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  const o1 = o(a, b, c), o2 = o(a, b, d), o3 = o(c, d, a), o4 = o(c, d, b);
  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
}

/** Cel mai apropiat punct al segmentului [a, b] de p. */
function peSegment(p: Pct, a: Pct, b: Pct): Pct {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
  return [a[0] + t * dx, a[1] + t * dy];
}

const dist = (a: Pct, b: Pct) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** Cea mai mică distanță între două poligoane și punctele în care se atinge;
 *  0 când se întretaie sau unul stă în celălalt. */
export function apropiere(a: Pct[], b: Pct[]): { d: number; p: Pct; q: Pct } {
  if (a.some((p) => inPoligon(p, b)) || b.some((p) => inPoligon(p, a))) return { d: 0, p: a[0], q: a[0] };
  let best = { d: Infinity, p: a[0], q: b[0] };
  for (let i = 0; i < a.length; i++) {
    const a0 = a[i], a1 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b0 = b[j], b1 = b[(j + 1) % b.length];
      if (segInters(a0, a1, b0, b1)) return { d: 0, p: a0, q: b0 };
      for (const [p, s0, s1, dinA] of [[a0, b0, b1, true], [a1, b0, b1, true], [b0, a0, a1, false], [b1, a0, a1, false]] as [Pct, Pct, Pct, boolean][]) {
        const q = peSegment(p, s0, s1);
        const d = dist(p, q);
        if (d < best.d) best = dinA ? { d, p, q } : { d, p: q, q: p };
      }
    }
  }
  return best;
}

/** Distanța de la un poligon la conturul lotului (0 dacă îl taie), și dacă e
 *  întreg înăuntru. */
export function fataDeContur(poly: Pct[], contur: Pct[]): { d: number; p: Pct; q: Pct; inauntru: boolean } {
  const inauntru = poly.every((p) => inPoligon(p, contur));
  let best = { d: Infinity, p: poly[0], q: contur[0] };
  for (let i = 0; i < poly.length; i++) {
    const a0 = poly[i], a1 = poly[(i + 1) % poly.length];
    for (let j = 0; j < contur.length; j++) {
      const b0 = contur[j], b1 = contur[(j + 1) % contur.length];
      if (segInters(a0, a1, b0, b1)) return { d: 0, p: a0, q: b0, inauntru: false };
      for (const [p, s0, s1, dinA] of [[a0, b0, b1, true], [a1, b0, b1, true], [b0, a0, a1, false], [b1, a0, a1, false]] as [Pct, Pct, Pct, boolean][]) {
        const q = peSegment(p, s0, s1);
        const d = dist(p, q);
        if (d < best.d) best = dinA ? { d, p, q } : { d, p: q, q: p };
      }
    }
  }
  return { ...best, inauntru };
}

/** Unghiurile laturilor unui contur, în [0, 180), distincte. */
export function unghiuriLaturi(contur: Pct[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < contur.length; i++) {
    const a = contur[i], b = contur[(i + 1) % contur.length];
    const u = ((Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI + 180) % 180;
    if (!out.some((x) => difUnghi(x, u) < 0.5)) out.push(u);
  }
  return out;
}

export function difUnghi(a: number, b: number): number {
  const d = Math.abs(a - b) % 180;
  return Math.min(d, 180 - d);
}

/** Lipirea unghiului: la 0° / 90° față de fiecare candidat (laturile lotului,
 *  celelalte volume), dacă e în rază; altfel neschimbat. */
export function lipesteUnghi(unghi: number, candidati: number[], raza = LIPIRE_UNGHI_DEG): number {
  let best: { d: number; u: number } | null = null;
  for (const c of candidati) {
    for (const k of [0, 90]) {
      const u = (c + k) % 180;
      const d = difUnghi(unghi, u);
      if (d <= raza && (!best || d < best.d)) best = { d, u };
    }
  }
  return best ? best.u : ((unghi % 180) + 180) % 180;
}

/** Punctul din lume → parametrul `a` (de-a lungul brațului) și `b` (pe
 *  perpendiculară) în reperul unui volum. */
export function inReperulVolumului(v: ParametriVolum, p: Pct): { a: number; b: number } {
  const c = Math.cos(rad(v.unghi_deg)), s = Math.sin(rad(v.unghi_deg));
  const dn = p[0] - v.centru[0], de = p[1] - v.centru[1];
  return { a: dn * c + de * s, b: -dn * s + de * c };
}

/** Un punct din reperul volumului, în lume. */
export function dinReperulVolumului(v: ParametriVolum, a: number, b: number): Pct {
  const c = Math.cos(rad(v.unghi_deg)), s = Math.sin(rad(v.unghi_deg));
  return [v.centru[0] + a * c - b * s, v.centru[1] + a * s + b * c];
}

/** Mânerele unui volum, în lume: capetele brațelor (lungimea), mijlocul
 *  laturii libere (lățimea) și cel de rotire. */
export interface Maner { fel: "brat" | "latime" | "rotire"; indice: number; p: Pct }

export function manere(v: ParametriVolum): Maner[] {
  const h = v.latime_m / 2, L = v.brate_m[0];
  const out: Maner[] = [];
  const s = v.parte;
  if (v.forma === "bara") {
    out.push({ fel: "brat", indice: 0, p: dinReperulVolumului(v, L / 2, 0) });
    out.push({ fel: "brat", indice: -1, p: dinReperulVolumului(v, -L / 2, 0) });
  } else if (v.forma === "L") {
    const e = v.capat;
    out.push({ fel: "brat", indice: 0, p: dinReperulVolumului(v, (-e * L) / 2, 0) });
    out.push({ fel: "brat", indice: 1, p: dinReperulVolumului(v, e * (L / 2 - h), s * (-h + v.brate_m[1])) });
  } else {
    out.push({ fel: "brat", indice: 1, p: dinReperulVolumului(v, -(L / 2 - h), s * (-h + v.brate_m[1])) });
    out.push({ fel: "brat", indice: 2, p: dinReperulVolumului(v, L / 2 - h, s * (-h + (v.brate_m[2] ?? v.brate_m[1]))) });
    out.push({ fel: "brat", indice: 0, p: dinReperulVolumului(v, 0, -s * h) });
  }
  out.push({ fel: "latime", indice: 0, p: dinReperulVolumului(v, 0, -s * h) });
  out.push({ fel: "rotire", indice: 0, p: dinReperulVolumului(v, 0, -s * (h + 12)) });
  return out;
}

/** Ce devine un volum când mânerul `m` e tras în `p`. */
export function trageManer(v: VolumSchita, m: Maner, p: Pct, candidatiUnghi: number[]): VolumSchita {
  const { a, b } = inReperulVolumului(v, p);
  const h = v.latime_m / 2, L = v.brate_m[0], s = v.parte;
  if (m.fel === "rotire") {
    const u = (Math.atan2(p[1] - v.centru[1], p[0] - v.centru[0]) * 180) / Math.PI;
    // mânerul stă pe perpendiculară (−s·w): unghiul brațului e cu 90° înainte
    const unghi = (((u + (s === 1 ? 90 : -90)) % 180) + 180) % 180;
    return cuParametri(v, { unghi_deg: lipesteUnghi(unghi, candidatiUnghi) });
  }
  if (m.fel === "latime") {
    return cuParametri(v, { latime_m: Math.abs(b) * 2 });
  }
  const brate = [...v.brate_m];
  if (m.indice === 0 && (v.forma === "U" || v.forma === "C")) {
    // la U/C brațul principal se lungește simetric, din mijloc
    return cuParametri(v, { brate_m: [Math.max(BRAT_MIN_M, 2 * Math.abs(a) + 0), brate[1], brate[2]] });
  }
  if (m.indice === 0 || m.indice === -1) {
    // capătul brațului principal: lungimea se schimbă și centrul se mută cu
    // jumătate, ca celălalt capăt să stea pe loc
    const e = m.indice === 0 ? 1 : -1;
    const fix = -e * (L / 2);
    const nouL = Math.max(BRAT_MIN_M, Math.min(BRAT_MAX_M, Math.abs(a - fix)));
    const centru = dinReperulVolumului(v, fix + e * (nouL / 2), 0);
    return cuParametri(v, { brate_m: [nouL, ...brate.slice(1)], centru });
  }
  brate[m.indice] = s * b + h;
  return cuParametri(v, { brate_m: brate });
}

/** Mutarea întregului volum cu un vector. */
export function muta(v: VolumSchita, dn: number, de: number): VolumSchita {
  return cuParametri(v, { centru: [v.centru[0] + dn, v.centru[1] + de] });
}

/** Volumul oglindit (aripile pe cealaltă parte) sau cu aripa la celălalt capăt. */
export function oglindeste(v: VolumSchita): VolumSchita {
  return cuParametri(v, { parte: v.parte === 1 ? -1 : 1 });
}
export function schimbaCapatul(v: VolumSchita): VolumSchita {
  return cuParametri(v, { capat: v.capat === 1 ? -1 : 1 });
}
